import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Phone, User, X } from 'lucide-react';

/**
 * The list behind one of the Overview's PC & café alerts (Insights & Alerts).
 *
 * Same rules as the alert emails — see backend app/tasks/pc_removal_alerts.py:
 *   missing      PCs not connecting for a week that have not come back
 *   quiet        cafés with no PC connecting for 3+ days
 *   uninstalled  PCs whose uninstall was reported in the last N days
 *   stuck        PCs used on 2+ days since their café's kiosk update that
 *                have not installed it (app/tasks/kiosk_version_alerts.py)
 *
 * Laid out for the reason it is opened — to call the café: one box per café,
 * worst first, with the café's phone and owner, and how long in plain words
 * (the exact time is on hover).
 *
 * Portalled to <body> so no ancestor's backdrop-filter can trap the fixed
 * overlay inside the dashboard card it was opened from.
 *
 * @param {'missing'|'quiet'|'uninstalled'|'stuck'} kind
 * @param {object} data  GET /internal/pc-alerts
 * @param {() => void} onClose
 */
export default function PcAlertsModal({ kind, data, onClose }) {
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // The café's Details page: its PCs with last-seen and install dates, which is
  // what these alerts are about (the café page is for remote commands).
  const openCafe = (cafeId) => {
    if (cafeId == null) return;
    onClose();
    navigate(`/cafes/${cafeId}`);
  };

  const contacts = new Map((data?.cafes || []).map((c) => [c.cafe_id, c]));
  const days = data?.uninstalled_days || 7;

  const view = {
    missing: {
      title: 'PCs missing',
      intro: 'These PCs have not connected for over a week while other PCs at their café were in use. '
        + 'A PC leaves this list as soon as it connects again.',
      groups: groupByCafe(data?.missing || []).map((g) => ({
        ...g,
        // Missing longest first, and the café with the longest-missing PC first.
        rows: g.rows.sort((a, b) => time(a.last_seen) - time(b.last_seen)),
        rank: Math.min(...g.rows.map((r) => time(r.last_seen))),
      })),
      count: (g) => `${g.rows.length} PC${g.rows.length === 1 ? '' : 's'}`,
      pills: (g) => g.rows.map((r) => (
        <span key={r.pc_id} className="badge badge--danger pc-alerts__pill" title={`Last seen ${exact(r.last_seen)}`}>
          {r.pc_name || `PC #${r.pc_id}`} · missing {since(r.last_seen)}
        </span>
      )),
    },
    quiet: {
      title: 'Cafés gone quiet',
      intro: 'No PC at these cafés has connected for over 3 days. '
        + 'A café leaves this list as soon as any of its PCs connects.',
      groups: (data?.quiet || []).map((r) => ({
        cafe_id: r.cafe_id, cafe_name: r.cafe_name, rows: [r], rank: time(r.last_seen),
      })),
      count: (g) => `${g.rows[0].pcs} PC${g.rows[0].pcs === 1 ? '' : 's'}`,
      pills: (g) => (
        <span className="badge badge--danger pc-alerts__pill" title={`Last PC connected ${exact(g.rows[0].last_seen)}`}>
          No PC for {since(g.rows[0].last_seen)}
        </span>
      ),
    },
    uninstalled: {
      title: `Uninstalled in the last ${days} days`,
      intro: 'noLag was uninstalled from these PCs. Only PCs on the newest noLag version report '
        + 'an uninstall, so older PCs will not show up here.',
      groups: groupByCafe(data?.uninstalled || []).map((g) => {
        // Still gone first, newest first within each.
        const rows = g.rows.sort((a, b) => (a.came_back - b.came_back) || (time(b.uninstalled_at) - time(a.uninstalled_at)));
        const stillGone = rows.some((r) => !r.came_back);
        // Cafés with a PC still gone first, then by the most recent uninstall.
        return { ...g, rows, rank: (stillGone ? 0 : 1e15) - Math.max(...rows.map((r) => time(r.uninstalled_at))) };
      }),
      count: (g) => `${g.rows.length} PC${g.rows.length === 1 ? '' : 's'}`,
      pills: (g) => g.rows.map((r) => {
        const name = r.pc_name || (r.pc_id != null ? `PC #${r.pc_id}` : 'A PC');
        const how = [
          `Uninstalled ${exact(r.uninstalled_at)}`,
          r.windows_user && `by Windows account ${r.windows_user}`,
          r.came_back && `· set up again ${exact(r.reinstalled_at)}`,
        ].filter(Boolean).join(' ');
        return r.came_back ? (
          <span key={`${r.pc_id}:${r.uninstalled_at}`} className="badge badge--success pc-alerts__pill" title={how}>
            {name} · came back
          </span>
        ) : (
          <span key={`${r.pc_id}:${r.uninstalled_at}`} className="badge badge--danger pc-alerts__pill" title={how}>
            {name} · still gone · {since(r.uninstalled_at)} ago
          </span>
        );
      }),
    },
    stuck: {
      title: 'PCs on an old version',
      intro: 'Customers used these PCs on 2 or more days since their café was told to update, and they '
        + 'still have not. A kiosk updates itself when idle, so something on the PC is stopping it. '
        + 'A PC leaves this list as soon as it updates.',
      groups: groupByCafe(data?.stuck || []).map((g) => ({
        ...g,
        rows: g.rows.sort((a, b) => String(a.pc_name || '').localeCompare(String(b.pc_name || ''), undefined, { numeric: true })),
        // The café with the most stuck PCs first.
        rank: -g.rows.length,
      })),
      count: (g) => `${g.rows.length} PC${g.rows.length === 1 ? '' : 's'} · should be on ${g.rows[0].expected}`,
      pills: (g) => g.rows.map((r) => (
        <span
          key={r.pc_id}
          className="badge badge--warning pc-alerts__pill"
          title={`Should be on ${r.expected} since ${exact(r.since)} · used on ${r.days_used} days since · last seen ${exact(r.last_seen)}`}
        >
          {r.pc_name || `PC #${r.pc_id}`} · on {r.version}
        </span>
      )),
    },
  }[kind];

  if (!view) return null;
  const groups = [...view.groups].sort((a, b) => a.rank - b.rank);
  const showChecked = kind !== 'uninstalled' && data?.checked_at;

  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal pc-alerts"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pc-alerts-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal__header">
          <h3 className="modal__title" id="pc-alerts-title">{view.title}</h3>
          <button type="button" className="modal__close" onClick={onClose} title="Close">
            <X size={18} />
          </button>
        </div>
        <div className="modal__body">
          <p className="pc-alerts__intro">{view.intro}</p>
          {groups.length === 0 ? (
            <p className="pc-alerts__empty">Nothing right now.</p>
          ) : (
            <div className="pc-alerts__list">
              {groups.map((g) => {
                const contact = contacts.get(g.cafe_id) || {};
                return (
                  <div key={g.cafe_id ?? g.cafe_name} className="pc-alerts__cafe-box">
                    <div className="pc-alerts__cafe-head">
                      {g.cafe_id != null ? (
                        <button type="button" className="pc-alerts__cafe" onClick={() => openCafe(g.cafe_id)}>
                          {g.cafe_name || `Café #${g.cafe_id}`}
                        </button>
                      ) : (
                        <span className="pc-alerts__cafe">{g.cafe_name || 'Unknown café'}</span>
                      )}
                      <span className="pc-alerts__count">{view.count(g)}</span>
                    </div>
                    <div className="pc-alerts__contact">
                      {contact.phone ? (
                        <a href={`tel:${contact.phone.replace(/[^\d+]/g, '')}`}>
                          <Phone size={13} /> {contact.phone}
                        </a>
                      ) : (
                        <span><Phone size={13} /> No phone saved</span>
                      )}
                      {contact.owner && <span><User size={13} /> {contact.owner} (owner)</span>}
                    </div>
                    <div className="pc-alerts__pills">{view.pills(g)}</div>
                  </div>
                );
              })}
            </div>
          )}
          {showChecked && (
            <p className="pc-alerts__checked">Checked every 30 minutes · last check {since(data.checked_at)} ago</p>
          )}
        </div>
      </div>

      <style>{`
        /* Only the list scrolls: the popup is a column capped by .modal's
           max-height, and the list takes whatever height is left — so a short
           screen never gets a second scrollbar on the popup itself. */
        .pc-alerts {
          width: min(640px, 92vw);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .pc-alerts .modal__header {
          flex-shrink: 0;
        }
        .pc-alerts .modal__body {
          display: flex;
          flex-direction: column;
          flex: 1 1 auto;
          min-height: 0;
        }
        .pc-alerts__intro {
          flex-shrink: 0;
          margin: 0 0 var(--space-4);
          font-size: var(--text-sm);
          color: var(--text-secondary);
        }
        .pc-alerts__list {
          display: flex;
          flex-direction: column;
          gap: var(--space-3);
          flex: 1 1 auto;
          min-height: 80px;
          max-height: 60vh;
          overflow-y: auto;
        }
        .pc-alerts__cafe-box {
          flex-shrink: 0;
        }
        .pc-alerts__cafe-box {
          padding: var(--space-3) var(--space-4);
          border: 1px solid var(--divider);
          border-radius: var(--radius-lg, 12px);
          min-width: 0;
        }
        .pc-alerts__cafe-head {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: var(--space-3);
        }
        .pc-alerts__cafe {
          padding: 0;
          background: none;
          border: none;
          color: var(--text-primary);
          font: inherit;
          font-weight: 600;
          cursor: pointer;
          text-align: left;
          overflow-wrap: anywhere;
        }
        button.pc-alerts__cafe:hover {
          color: #FE7B00;
          text-decoration: underline;
        }
        .pc-alerts__count {
          flex-shrink: 0;
          font-size: var(--text-xs);
          color: var(--text-tertiary);
        }
        .pc-alerts__contact {
          display: flex;
          flex-wrap: wrap;
          gap: var(--space-1) var(--space-4);
          margin-top: var(--space-1);
          font-size: var(--text-xs);
          color: var(--text-secondary);
        }
        .pc-alerts__contact a,
        .pc-alerts__contact span {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          color: inherit;
          text-decoration: none;
        }
        .pc-alerts__contact a:hover {
          color: var(--text-primary);
          text-decoration: underline;
        }
        .pc-alerts__pills {
          display: flex;
          flex-wrap: wrap;
          gap: var(--space-2);
          margin-top: var(--space-3);
        }
        .pc-alerts__pill {
          max-width: 100%;
          white-space: normal;
          overflow-wrap: anywhere;
        }
        .pc-alerts__empty {
          margin: 0;
          color: var(--text-tertiary);
        }
        .pc-alerts__checked {
          flex-shrink: 0;
          margin: var(--space-4) 0 0;
          font-size: var(--text-xs);
          color: var(--text-tertiary);
        }
      `}</style>
    </div>,
    document.body,
  );
}

function groupByCafe(rows) {
  const byCafe = new Map();
  for (const r of rows) {
    const key = r.cafe_id ?? `name:${r.cafe_name}`;
    if (!byCafe.has(key)) byCafe.set(key, { cafe_id: r.cafe_id, cafe_name: r.cafe_name, rows: [] });
    byCafe.get(key).rows.push(r);
  }
  return [...byCafe.values()];
}

function time(iso) {
  const t = iso ? new Date(iso).getTime() : NaN;
  return Number.isNaN(t) ? 0 : t;
}

/** "29 Sept, 4:52 pm" in the viewer's time. */
function exact(iso) {
  if (!iso) return 'unknown';
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return 'unknown';
  return when.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}

/** How long since `iso`, in plain words: "under an hour", "5 h", "2 days". */
function since(iso) {
  const t = time(iso);
  if (!t) return 'a while';
  const hours = Math.max(0, (Date.now() - t) / 3600000);
  if (hours < 1) return 'under an hour';
  if (hours < 24) return `${Math.floor(hours)} h`;
  const d = Math.floor(hours / 24);
  return `${d} day${d === 1 ? '' : 's'}`;
}
